/**
 * API client for admin Global-e migration and portal actions (migrate, remigrate, activate, open RMA JSON export/upload).
 * Includes `postDownload` for JSON POSTs whose response is read as `blob` (file vs JSON message), matching former HttpService behavior.
 */
import axios, { AxiosResponse } from "axios";
import { injectable } from "inversify";
import errorModalStore from "../../components/error-modal/error-modal-store";
import { TYPE, rootContainer } from "../container/root";
import { isSet } from "../../models/model-types-methods";
import { IApiResponse, IHttpService, generateContextId } from "../http-service";
import {
    IMigrateGlobalEMerchantsRequestData
} from "../../pages/admins/migrate-global-e-merchants/stores/migrate-global-e-merchants-page-store";

export enum GlobalEEnvironment {
    QA = "QA",
    QA_HF = "QA_HF",
    DR = "DR",
    INT = "INT",
    LT = "LT",
    STG = "STG",
    PROD = "PRODUCTION",
}

export interface IMigrateGlobaleShopifyStoreRequest {
    portal_id: number;
}

export interface IRemigrateGlobalEMerchantRequest {
    portal_id: number;
    merchantId: string;
    environment: GlobalEEnvironment;
}

export interface IMigrateGlobaleShopifyStoreResponse {
    num_deleted_reasons: number;
    num_deleted_resolutions: number;
    num_deleted_return_methods: number;
}

export interface IGlobaleStoreMetaFields {
    merchant_id: number;
    env: GlobalEEnvironment;
}

export interface ILastCompletedShopMigrationAttempt {
    migration_finished_at: string | null;
    status: "completed" | "completed_with_errors";
}

export interface IActivateGlobaleMerchantResponse {
    successful_activation: boolean;
}

export interface IPlatformTransformationRequest {
    old_portal_id: number;
    new_portal_id: number;
}

export type PlatformTransformationStepStatus = "completed" | "failed" | "skipped" | null;

export interface IPlatformTransformationAttemptStep {
    step_name: string;
    status: PlatformTransformationStepStatus;
    failure_reason: string | null;
    finished_at: string | null;
    created_at: string | null;
}

export interface IPlatformTransformationAttempt {
    id: number;
    status: string;
    failure_reason: string | null;
    steps: IPlatformTransformationAttemptStep[];
}

export interface IPlatformTransformationResponse {
    success: boolean;
    old_portal_id: number;
    new_portal_id: number;
    attempt: IPlatformTransformationAttempt;
}

export interface ITriggerRmasImportResponse {
    attempt_id: number;
    status: string;
    message: string;
}

export interface ISupportedGlobalEEnvironment {
    value: GlobalEEnvironment;
    label: string;
}

export interface ISupportedGlobalEEnvironmentsResponse {
    environments: ISupportedGlobalEEnvironment[];
}

export type DownloadOpenRmasResult =
    | { kind: "file"; blob: Blob; filename: string }
    | { kind: "message"; message: string };

const DOWNLOAD_STATUS_MESSAGE_MAX_BYTES = 64 * 1024;

function filenameFromContentDisposition(header: string | undefined, fallback: string): string {
    if (!header) {
        return fallback;
    }
    const star = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
    if (star?.[1]) {
        try {
            return decodeURIComponent(star[1].trim());
        } catch {
            return star[1].trim();
        }
    }
    const quoted = /filename="([^"]+)"/i.exec(header);
    if (quoted?.[1]) {
        return quoted[1].trim();
    }
    const plain = /filename=([^;\n]+)/i.exec(header);
    if (plain?.[1]) {
        return plain[1].replace(/["']/g, "").trim();
    }
    return fallback;
}

/**
 * Detects status payloads from the no-export API path, e.g. `{ "message": "..." }`.
 * Status responses are JSON objects with a string `message`; open RMA exports are JSON
 * arrays. Classification therefore keys off the object-vs-array shape rather than an
 * exact key set, so extra status fields (e.g. `code`) or longer messages are still
 * surfaced to the admin instead of being downloaded as a junk file. The size cap only
 * guards against parsing an unexpectedly large body.
 */
export function parseDownloadStatusMessage(text: string): string | null {
    // Exports are JSON arrays; only objects can be status messages. Cheap prefix check
    // avoids fully parsing a large export array just to reject it.
    if (!text.trimStart().startsWith("{")) {
        return null;
    }
    if (text.length > DOWNLOAD_STATUS_MESSAGE_MAX_BYTES) {
        return null;
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return null;
    }
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        return null;
    }
    const record = parsed as Record<string, unknown>;
    if (typeof record.message !== "string") {
        return null;
    }
    return record.message;
}

async function readBlobAsText(blob: Blob): Promise<string> {
    if (typeof blob.text === "function") {
        return blob.text();
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error ?? new Error("Failed to read blob"));
        reader.readAsText(blob);
    });
}

@injectable()
class AdminMigrateGlobalEMerchantsService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    /** GET /rms-api/admin-supported-global-e-envs — environments allowed on this deployment. */
    public async fetchSupportedGlobalEEnvironments(): Promise<ISupportedGlobalEEnvironmentsResponse> {
        const res = await this.httpService.get<IApiResponse<ISupportedGlobalEEnvironmentsResponse>>(
            "/rms-api/admin-supported-global-e-envs"
        );
        return res.payload;
    }

    public async migrateGlobalEMerchants(request: IMigrateGlobalEMerchantsRequestData): Promise<void> {
        await this.httpService.post<IApiResponse<void>>(
            "/rms-api/admin-migrate-global-e-merchants",
            {
              ...request
            }
        );
    }

    public async migrateGlobalEShopifyPlatformStore(request: IMigrateGlobaleShopifyStoreRequest): Promise<IMigrateGlobaleShopifyStoreResponse> {
        const res = await this.httpService.post<IApiResponse<IMigrateGlobaleShopifyStoreResponse>>(
            "/rms-api/admin-migrate-shopify-platform-globale-merchants",
            {
              ...request
            }
        );
        return res.payload;
    }

    /** POST /rms-api/admin-remigrate-global-e-merchant — re-run Global-E platform migration for an existing portal. */
    public async remigrateGlobalEMerchant(request: IRemigrateGlobalEMerchantRequest): Promise<void> {
        await this.httpService.post<IApiResponse<{ success: boolean }>>(
            "/rms-api/admin-remigrate-global-e-merchant",
            {
                portal_id: request.portal_id,
                merchantId: request.merchantId,
                environment: request.environment,
            }
        );
    }
    
    public async fetchGlobaleStoreMetaFields(portalId: number): Promise<IGlobaleStoreMetaFields> {
        const res = await this.httpService.get<IApiResponse<IGlobaleStoreMetaFields>>(
            `/rms-api/admin-migrate-shopify-get-globale-metafields/${portalId}`
        );
        return res.payload;
    }

    public async fetchLastCompletedShopMigrationAttempt(
        portalId: number
    ): Promise<ILastCompletedShopMigrationAttempt | null> {
        const res = await this.httpService.get<
            IApiResponse<{ last_completed_attempt: ILastCompletedShopMigrationAttempt | null }>
        >(`/rms-api/admin-migrate-last-completed-migration-attempt/${portalId}`);
        return res.payload.last_completed_attempt;
    }

    public async activateGlobaleMerchant(request: IMigrateGlobaleShopifyStoreRequest): Promise<IActivateGlobaleMerchantResponse> {
        const res = await this.httpService.post<IApiResponse<IActivateGlobaleMerchantResponse>>(
            '/rms-api/admin-activate-globale-merchant',
            request
        );
        return res.payload;
    }

    /** POST /rms-api/admin-platform-transformation — cutover Global-e merchant onto a second Shopify store. */
    public async runPlatformTransformation(
        request: IPlatformTransformationRequest
    ): Promise<IPlatformTransformationResponse> {
        const res = await this.httpService.post<IApiResponse<IPlatformTransformationResponse>>(
            "/rms-api/admin-platform-transformation",
            request
        );
        return res.payload;
    }

    /**
     * POST /rms-api/admin-download-open-rmas-json — JSON file attachment or JSON body with `message` when no export is ready.
     */
    public async downloadOpenRmas(portalId: number): Promise<DownloadOpenRmasResult> {
        const response = await this.postDownload("/rms-api/admin-download-open-rmas-json", {
            portal_id: portalId,
        });

        const defaultFilename = `${portalId}_open-rmas.json`;
        const contentType = String(response.headers["content-type"] ?? "");
        if (contentType.includes("application/json")) {
            const text = await readBlobAsText(response.data as Blob);
            const statusMessage = parseDownloadStatusMessage(text);
            if (statusMessage !== null) {
                return { kind: "message", message: statusMessage };
            }
            const disposition = response.headers["content-disposition"] as string | undefined;
            const filename = filenameFromContentDisposition(disposition, defaultFilename);
            return { kind: "file", blob: new Blob([text], { type: contentType }), filename };
        }

        const disposition = response.headers["content-disposition"] as string | undefined;
        const filename = filenameFromContentDisposition(disposition, defaultFilename);
        return { kind: "file", blob: response.data as Blob, filename };
    }

    /**
     * POST /rms-api/admin-trigger-rmas-import — queue V2 Global-e open RMA import for an activated portal.
     */
    public async triggerRmasImport(portalId: number): Promise<ITriggerRmasImportResponse> {
        const res = await this.httpService.post<IApiResponse<ITriggerRmasImportResponse>>(
            "/rms-api/admin-trigger-rmas-import",
            { portal_id: portalId }
        );
        return res.payload;
    }

    /**
     * POST /rms-api/admin-upload-open-rmas-json — multipart JSON upload; queues V2 import job.
     */
    public async uploadOpenRmasJson(portalId: number, file: File): Promise<ITriggerRmasImportResponse> {
        const formData = new FormData();
        formData.append("portal_id", String(portalId));
        formData.append("file", file);
        const res = await this.httpService.postFormData<IApiResponse<ITriggerRmasImportResponse>>(
            "/rms-api/admin-upload-open-rmas-json",
            formData
        );
        return res.payload;
    }

    /**
     * POST with JSON body; response is read as `blob` and returned with headers (e.g. file download vs JSON message).
     * Mirrors the former `HttpService` POST + `responseType: 'blob'` path without delegating to that class.
     */
    private async postDownload(path: string, data?: unknown): Promise<AxiosResponse<Blob>> {
        const baseUrl = this.httpService.getBaseUrl() ?? "";
        const headers: Record<string, string> = {
            Accept: "*/*",
            "Content-Type": "application/json",
            "X-Request-Id": generateContextId(),
        };
        try {
            return await axios<Blob>(`${baseUrl}${path}`, {
                method: "POST",
                headers,
                data: data !== undefined && data !== null ? JSON.stringify(data) : undefined,
                responseType: "blob",
                withCredentials: true,
            });
        }
        catch (error: unknown) {
            const err = error as { response?: AxiosResponse; request?: unknown; message?: string };
            const responseData = await (err.response ? this.parseBlobResponseIntoJSON(err.response) : undefined);

            this.showResponseErrorInModal(responseData?.errors, responseData?.code || err?.response?.status, err.message);

            throw error;
        }
    }

    private showResponseErrorInModal(responseData?: Record<string, any>, responseStatusCode?: number, errorMessage?: string): void {

        if (isSet(responseData)) {
            console.error("response", responseData);
            const code = responseData?.code || responseStatusCode || 500;
            const validationErrors = responseData?.errors;

            if (validationErrors) {
                for (const i in validationErrors) {
                    errorModalStore.add({
                        title: validationErrors[i][0] ?? "Server Error",
                        code,
                    });
                }
            }
            else {
                errorModalStore.add({
                    ...responseData,
                    title: JSON.stringify(responseData) ?? "Server Error",
                    code,
                });
            }
        }
        else {
            errorModalStore.add({
                title: errorMessage ?? "Error",
            });
        }
    }

    /**
     * When `responseType` is `blob`, Axios exposes JSON bodies as a `Blob`.
     * Uses `Content-Type` and text-then-JSON.parse pattern to get response data.
     */
    private async parseBlobResponseIntoJSON(response: AxiosResponse): Promise<Record<string, any>> {
        const { data } = response;
        if (!(data instanceof Blob)) {
            return data as Record<string, any>;
        }
        const contentType = String(response.headers["content-type"] ?? "");
        const text = await readBlobAsText(data);
        if (!text.trim()) {
            return {};
        }
        const isJsonContentType = contentType.includes("application/json");
        if (!isJsonContentType) {
            return {};
        }
        try {
            return JSON.parse(text) as Record<string, any>;
        } catch {
            return {};
        }
    }
}

function formatHttpErrorEndpoint(error: { config?: { method?: string; url?: string } } | unknown): string {
    const e = error as { config?: { method?: string; url?: string } };
    return [String(e.config?.method ?? "").toUpperCase(), e.config?.url].filter(Boolean).join(" ");
}

export default AdminMigrateGlobalEMerchantsService;
